import React, { useRef, useState, useCallback, useEffect } from 'react';
import * as fabric from 'fabric';
import { A4_WIDTH, A4_HEIGHT, PAGE_MARGIN, BASE_SNAP_THRESHOLD } from '../constants';

export interface GuideLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function useSnapGuides(
  fabricCanvasRef: React.RefObject<fabric.Canvas | null>,
  zoomLevel: number
) {
  const [snapEnabled, setSnapEnabled] = useState<boolean>(true);
  const snapEnabledRef = useRef<boolean>(true);
  snapEnabledRef.current = snapEnabled;

  const guideLinesRef = useRef<GuideLine[]>([]);
  const cachedXTargetsRef = useRef<number[] | null>(null);
  const cachedYTargetsRef = useRef<number[] | null>(null);

  // Initialize cached snap targets when user starts dragging an object
  const prepareSnapTargets = useCallback((movingObject: fabric.FabricObject) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const xTargets = [
      PAGE_MARGIN,            // Left margin
      A4_WIDTH / 2,           // Horizontal center
      A4_WIDTH - PAGE_MARGIN, // Right margin
    ];

    const yTargets = [
      PAGE_MARGIN,             // Top margin
      A4_HEIGHT / 2,           // Vertical center
      A4_HEIGHT - PAGE_MARGIN, // Bottom margin
    ];

    canvas.getObjects().forEach((obj) => {
      if (obj === movingObject || obj.type === 'activeSelection') return;
      const b = obj.getBoundingRect();
      xTargets.push(b.left, b.left + b.width / 2, b.left + b.width);
      yTargets.push(b.top, b.top + b.height / 2, b.top + b.height);
    });

    cachedXTargetsRef.current = xTargets;
    cachedYTargetsRef.current = yTargets;
  }, [fabricCanvasRef]);

  const clearSnapTargets = useCallback(() => {
    cachedXTargetsRef.current = null;
    cachedYTargetsRef.current = null;
    if (guideLinesRef.current.length > 0) {
      guideLinesRef.current = [];
      fabricCanvasRef.current?.renderAll();
    }
  }, [fabricCanvasRef]);

  // Handle object movement with snap logic
  const handleObjectMoving = useCallback((e: { target?: fabric.FabricObject }) => {
    if (!snapEnabledRef.current) return;
    const target = e.target;
    const canvas = fabricCanvasRef.current;
    if (!target || !canvas) return;

    // Prepare target cache if not built yet for this drag session
    if (!cachedXTargetsRef.current || !cachedYTargetsRef.current) {
      prepareSnapTargets(target);
    }

    const xTargets = cachedXTargetsRef.current || [];
    const yTargets = cachedYTargetsRef.current || [];

    const targetBounds = target.getBoundingRect();
    const tLeft = targetBounds.left;
    const tTop = targetBounds.top;
    const tWidth = targetBounds.width;
    const tHeight = targetBounds.height;

    const tRight = tLeft + tWidth;
    const tBottom = tTop + tHeight;
    const tCenterX = tLeft + tWidth / 2;
    const tCenterY = tTop + tHeight / 2;

    const newLines: GuideLine[] = [];
    let snappedX = false;
    let snappedY = false;

    // Adjust threshold based on zoom level for visual consistency
    const effectiveThreshold = BASE_SNAP_THRESHOLD / Math.max(0.1, zoomLevel);

    // Check horizontal snap (vertical guide lines)
    for (const xVal of xTargets) {
      if (!snappedX && Math.abs(tLeft - xVal) < effectiveThreshold) {
        target.set({ left: (target.left || 0) + (xVal - tLeft) });
        snappedX = true;
        newLines.push({ x1: xVal, y1: 0, x2: xVal, y2: A4_HEIGHT });
      } else if (!snappedX && Math.abs(tCenterX - xVal) < effectiveThreshold) {
        target.set({ left: (target.left || 0) + (xVal - tCenterX) });
        snappedX = true;
        newLines.push({ x1: xVal, y1: 0, x2: xVal, y2: A4_HEIGHT });
      } else if (!snappedX && Math.abs(tRight - xVal) < effectiveThreshold) {
        target.set({ left: (target.left || 0) + (xVal - tRight) });
        snappedX = true;
        newLines.push({ x1: xVal, y1: 0, x2: xVal, y2: A4_HEIGHT });
      }
    }

    // Check vertical snap (horizontal guide lines)
    for (const yVal of yTargets) {
      if (!snappedY && Math.abs(tTop - yVal) < effectiveThreshold) {
        target.set({ top: (target.top || 0) + (yVal - tTop) });
        snappedY = true;
        newLines.push({ x1: 0, y1: yVal, x2: A4_WIDTH, y2: yVal });
      } else if (!snappedY && Math.abs(tCenterY - yVal) < effectiveThreshold) {
        target.set({ top: (target.top || 0) + (yVal - tCenterY) });
        snappedY = true;
        newLines.push({ x1: 0, y1: yVal, x2: A4_WIDTH, y2: yVal });
      } else if (!snappedY && Math.abs(tBottom - yVal) < effectiveThreshold) {
        target.set({ top: (target.top || 0) + (yVal - tBottom) });
        snappedY = true;
        newLines.push({ x1: 0, y1: yVal, x2: A4_WIDTH, y2: yVal });
      }
    }

    if (target.setCoords) target.setCoords();
    guideLinesRef.current = newLines;
    canvas.renderAll();
  }, [fabricCanvasRef, prepareSnapTargets, zoomLevel]);

  // Render guide lines on top of canvas overlay
  const renderGuideLines = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || guideLinesRef.current.length === 0) return;

    const ctx = canvas.getSelectionContext ? canvas.getSelectionContext() : canvas.getContext();
    if (!ctx) return;

    ctx.save();
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 1.5 / Math.max(0.1, zoomLevel);
    ctx.setLineDash([4, 4]);

    guideLinesRef.current.forEach((line) => {
      ctx.beginPath();
      ctx.moveTo(line.x1, line.y1);
      ctx.lineTo(line.x2, line.y2);
      ctx.stroke();
    });

    ctx.restore();
  }, [fabricCanvasRef, zoomLevel]);

  return {
    snapEnabled,
    setSnapEnabled,
    handleObjectMoving,
    clearSnapTargets,
    renderGuideLines,
  };
}
